import { domain } from './domain'
import {
  closeProfileModal,
  firstLastNameChange,
  getPaymentWalletFx,
  getPaymentWallet,
  resetPaymentMethod,
  nicknameChange,
  openProfileModal,
  selectProfileModalTab,
  setMainCurrency,
  setTopUpCurrency,
  setWithdrawAmount,
  setWithdrawCurrency,
  setWithdrawDestinationWallet,
  setMainCurrencyFx,
  passwordChangeEvent,
  confirmPasswordChangeEvent,
  oldPasswordChangeEvent,
  savePassword,
  savePasswordFx,
  getAvailableMethodsFx,
  getAvailableMethods,
  setPaymentMethod,
  setTopUpAmount,
  paymentConfirm,
  paymentConfirmFx,
  withdrawRequest,
  withdrawRequestFx,
  paymentConfirmPending,
} from './events'
import { PaymentWallet, ProfileModalTab, UserWalletType } from './types'
import { Currency } from '@/4_entities/currency'
import { combine, forward, guard, sample } from 'effector'
import { PaymentWalletGate } from './gate'
import { $fetchedPaymentSystems } from '@/2_widgets/profile-block'
import { PaymentSystem } from '@/2_widgets/profile-block/types'
import { NotificationService } from '@/7_services/notification-service'
import i18next from 'i18next'
import { logout } from '@/7_services/user/model'

export const $isProfileModalOpened = domain
  .createStore<boolean>(false)
  .on(openProfileModal, () => true)
  .on(selectProfileModalTab, () => true)
  .reset(closeProfileModal)

export const $isPaymentConfirmPending = domain
  .createStore<boolean>(false)
  .on(paymentConfirmPending, (_, payload) => payload)

export const $profileModalSelectedTab = domain
  .createStore<ProfileModalTab>(ProfileModalTab.Profile)
  .on(openProfileModal, (_, payload) => payload || ProfileModalTab.Profile)
  .on(selectProfileModalTab, (_, payload) => payload)
  .reset(closeProfileModal)

// todo to user profile
export const $nicknameInput = domain
  .createStore<string>('')
  .on(nicknameChange, (_, payload) => payload)
  .reset(closeProfileModal)

// todo to user profile
export const $firstLastName = domain
  .createStore<string>('')
  .on(firstLastNameChange, (_, payload) => payload)
  .reset(closeProfileModal)

// todo to profile? or currency?
export const $selectedMainCurrency = domain
  .createStore<number | null>(null)
  .on(setMainCurrency, (_, payload) => payload)

// todo to user profile
export const $userWallet = domain.createStore<UserWalletType>([])

export const $topUpSelectedCurrency = domain
  .createStore<Currency['CurrencyID'] | null>(null)
  .on(setTopUpCurrency, (_, payload) => payload)

export const $paymentSystem = domain
  .createStore<PaymentSystem | null>(null)
  .on(setTopUpCurrency, (_, payload) =>
    $fetchedPaymentSystems
      .getState()
      .find((sys) => sys.system_currency === payload)
  )
  .on(setWithdrawCurrency, (_, payload) =>
    $fetchedPaymentSystems
      .getState()
      .find((sys) => sys.system_currency === payload)
  )
  .reset(closeProfileModal)

export const $withdrawSelectedCurrency = domain
  .createStore<Currency['CurrencyID'] | null>(null)
  .on(setWithdrawCurrency, (_, payload) => payload)
  .reset(selectProfileModalTab, closeProfileModal)

export const $withdrawDestinationWallet = domain
  .createStore<string>('')
  .on(setWithdrawDestinationWallet, (_, payload) => payload)
  .reset(closeProfileModal, setWithdrawCurrency)

export const $selectedWithdrawAmount = domain
  .createStore<number>(0)
  .on(setWithdrawAmount, (_, payload) => payload)
  .reset(closeProfileModal, setWithdrawCurrency)

export const $paymentMethods = domain
  .createStore<string[]>([])
  .on(getAvailableMethodsFx.doneData, (_, payload) => payload.methods)
  .reset(resetPaymentMethod, closeProfileModal)

export const $selectedPaymentMethod = domain
  .createStore<string | null>('')
  .on(setPaymentMethod, (_, payload) => payload)
  .reset(resetPaymentMethod, closeProfileModal)

export const $topUpAmount = domain
  .createStore<number>(0)
  .on(setTopUpAmount, (_, payload) => payload)
  .reset(closeProfileModal)

forward({
  from: PaymentWalletGate.open,
  to: getPaymentWallet,
})

forward({
  from: setMainCurrency,
  to: setMainCurrencyFx,
})

guard({
  source: getPaymentWallet,
  filter: getPaymentWalletFx.pending.map((pending) => !pending),
  target: getPaymentWalletFx,
})

guard({
  source: getAvailableMethods,
  filter: getAvailableMethodsFx.pending.map((pending) => !pending),
  target: getAvailableMethodsFx,
})

guard({
  source: paymentConfirm,
  filter: paymentConfirmFx.pending.map((pending) => !pending),
  target: paymentConfirmFx,
})

paymentConfirmFx.pending.watch((state) => {
  paymentConfirmPending(state)
})

getPaymentWalletFx.fail.watch(({ error }: { error: Error }) => {
  NotificationService.error(error.message)
})

sample({
  clock: paymentConfirmFx.doneData,
  fn: () => {
    NotificationService.success(
      `${i18next.t(
        'notifications.payment.success'
      )} ${$getPaymentWallet.getState()?.order_number}`
    )
    setTopUpCurrency(null)
    resetPaymentMethod()
    setTopUpAmount(0)
  },
})

sample({
  clock: setPaymentMethod,
  source: {
    bank_or_method: $selectedPaymentMethod,
    sum: $topUpAmount,
    currency: $topUpSelectedCurrency,
    user_id: '',
    payment_type: 7,
  },
  fn: (data: {
    bank_or_method: string
    sum: number
    currency: Currency['CurrencyID'] | null
    user_id: string
    payment_type: number
  }) => {
    return {
      bank_or_method: data.bank_or_method,
      sum: data.sum * 100,
      currency: data.currency,
      user_id: data.user_id,
      payment_type: data.payment_type,
    }
  },
  filter: getPaymentWalletFx.pending.map((pending) => !pending),
  target: getPaymentWalletFx,
})

guard({
  source: withdrawRequest,
  filter: withdrawRequestFx.pending.map((pending) => !pending),
  target: withdrawRequestFx,
})

export const $getPaymentWallet = domain
  .createStore<PaymentWallet | null>(null)
  .on(getPaymentWalletFx.doneData, (_, payload) => payload)
  .reset(closeProfileModal, resetPaymentMethod)

export const $getPaymentWalletNumber = domain
  .createStore<string>('')
  .on(getPaymentWalletFx.doneData, (_, payload) => payload.wallet_number)
  .reset(
    selectProfileModalTab,
    closeProfileModal,
    setTopUpCurrency,
    getPaymentWalletFx.fail
  )

getPaymentWalletFx.fail.watch(({ error }: { error: Error }) => {
  NotificationService.error(error.message)
})

export const $passwordInput = domain
  .createStore<string>('')
  .on(passwordChangeEvent, (_, payload) => payload)
  .reset(closeProfileModal)

export const $oldPasswordInput = domain
  .createStore<string>('')
  .on(oldPasswordChangeEvent, (_, payload) => payload)
  .reset(closeProfileModal)

export const $confirmPasswordInput = domain
  .createStore<string>('')
  .on(confirmPasswordChangeEvent, (_, payload) => payload)
  .reset(closeProfileModal)

const $isPasswordValid = combine(
  $passwordInput,
  $confirmPasswordInput,
  (pass, confirmPass) => pass === confirmPass
)

guard({
  clock: savePassword,
  filter: $isPasswordValid,
  source: {
    old_password: $oldPasswordInput,
    new_password: $passwordInput,
  },
  target: savePasswordFx,
})

logout.watch(() => {
  closeProfileModal()
  setTopUpCurrency(null)
  resetPaymentMethod()
  setTopUpAmount(0)
})
